import { gsap } from 'gsap';
import { CustomEase } from 'gsap/CustomEase';

gsap.registerPlugin(CustomEase);

// SECTION 01「KV」の登場タイムライン。
// KVはファーストビューなのでスクロール監視は使わず、読み込み直後に1回だけ再生する。
// 再生順はユーザー指定：
//   タイトル → キャッチコピー3行 → 動画サムネ → 吹き出し
//   → トマト＋集合写真＋リード文（同時） → 紙吹雪
// 初期状態（opacity:0）は _report.scss 側が持っている。
// JSで敷くと再生開始まで一瞬見えてしまうため、ここでは opacity を伏せないこと。

// ---- 調整値はここだけ ----
// 主役はキャッチコピー3行と動画サムネ（ユーザー指定 2026-09-15）。
// トマト・集合写真はそれを受ける締めの装飾なので、こちらを目立たせる溜めは置かない。
// コピー3行の行間。1行ずつ独立した拍として立たせる。詰めると塊に潰れて読めなくなる
const COPY_STAGGER = .2;
// fadeInUp 系 / bounceIn 系それぞれの尺
const DUR_FADE = .6;
const DUR_BOUNCE = .6;
// 主役（コピー3行・サムネ）だけ fadeInUp の尺を延ばして丁寧に見せる。
// タイトル・吹き出しは DUR_FADE のままにして、動きの重さで主従を分ける
const DUR_FADE_FOCUS = .8;
// 各ステップの間隔（前のステップの開始からの秒数。サムネだけはコピー3行目の出始めが基準）。
// 狙いは
//   タイトル → コピーを1行ずつ拍で立てる → 間を取ってサムネ単体の見せ場
//   → 吹き出しは付属で直後 → トマトたちは溜めずに流れで締める → 着地を見せてから紙吹雪
// 紙吹雪は 2.5s 以内に終わらせること（ファーストビューなので長いと待たされる）
const GAP = {
  // タイトル → コピー1行目。行間と同じ拍で入って、そのまま3行を刻む
  copy: COPY_STAGGER,
  // コピー3行目の出始め → サムネ。行間より広く空けて3行のフレーズを切り、サムネを単体の見せ場にする
  movie: .3,
  // サムネ → 吹き出し。拍より短く寄せて「サムネの付属品」に見せる。独立した拍として競わせない
  bubble: .1,
  // 吹き出し → トマトたち。溜めは置かず拍どおりに続けて、前から流れてくる締めにする
  pop: COPY_STAGGER,
  // トマトたち → 紙吹雪。bounceIn は終盤まで揺れているので尺いっぱい待って着地を見せる
  // （紙吹雪の前のワンテンポはユーザー指定なので削らない）
  confetti: DUR_BOUNCE,
};
// fadeInUp の持ち上げ量。_variable.scss の @keyframes fadeInUp と同じ 30px
const RISE = 30;
// 画像の復号を待つ上限。window load は中のYouTube iframe に引きずられるうえ、
// KVの画像はほぼ loading="lazy" で load を待たせてくれないので自前で待つ
const IMG_WAIT_CAP = 2000;

// サイト共通の bounceIn の曲線（news / archive / home / article / Action と同じ）。
// ease / ease-in-out は 1 を超えないので、行き過ぎてから戻す動きはこの曲線でしか出ない
const EASE_BOUNCE = CustomEase.create('reportKvBounce', 'M0,0 C0.68,-0.55 0.265,1.55 1,1');

const kvScene = {
  init() {
    const kv = document.querySelector('.p-reportKv');
    if (!kv) return;

    const q = (sel) => gsap.utils.toArray(kv.querySelectorAll(sel));

    // タイトルは h1 そのものではなく中の2枚を動かす。
    // h1 は position 指定が無く、transform を当てると badge / logo（__inner 基準の絶対配置）の
    // 包含ブロックが h1 に移ってしまう
    const title = q('.p-reportKv__badge, .p-reportKv__logo');
    const copy = q('.p-reportKv__copyItem');
    const movie = q('.p-reportKv__movie');
    const bubble = q('.p-reportKv__bubble');
    // 集合写真はネームタグごと1つの塊で拡大する（ユーザー指定：タグは groupImg と完全に同時）。
    // リード文も同じ組。タイミングだけでなくイージング・尺まで揃える指定なので、
    // 文字だからと別tweenに分けないこと（ユーザー指定 2026-09-15）
    const pop = q('.p-reportKv__tomato, .p-reportKv__group, .p-reportKv__lead');
    // PC用の l / r とSP用の1枚もの。表示されない側は display:none なので当たっても無害
    const confetti = q('.p-reportKv__confetti');

    const all = [...title, ...copy, ...movie, ...bubble, ...pop, ...confetti];
    if (all.length === 0) return;

    // 動きを減らす設定のときは最終状態だけ作って終わり
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      gsap.set(all, { opacity: 1 });
      return;
    }

    const play = () => {
      const fade = { opacity: 1, y: 0, duration: DUR_FADE, ease: 'power2.out' };
      // 主役用。動きは同じで尺だけ長い
      const fadeFocus = { ...fade, duration: DUR_FADE_FOCUS };
      const bounce = { opacity: 1, scale: 1, duration: DUR_BOUNCE, ease: EASE_BOUNCE };
      const tl = gsap.timeline();

      // `<x` は直前の tween の開始から x 秒後
      tl.fromTo(title, { opacity: 0, y: RISE }, fade, 0)
        // 3行はまとめて1本の tween。行間は COPY_STAGGER で1行ずつ立てる
        .fromTo(copy, { opacity: 0, y: RISE }, { ...fadeFocus, stagger: COPY_STAGGER }, `<${GAP.copy}`)
        // `<` はコピー1行目の開始を指すので、3行目の出始め（2行ぶん後）を基準にして間を取ってからサムネイルへ
        .fromTo(movie, { opacity: 0, y: RISE }, fadeFocus, `<${COPY_STAGGER * 2 + GAP.movie}`)
        .fromTo(bubble, { opacity: 0, y: RISE }, fade, `<${GAP.bubble}`)
        // トマト・集合写真・リード文は同じ組で同時に出る
        .fromTo(pop, { opacity: 0, scale: .3 }, bounce, `<${GAP.pop}`)
        .fromTo(confetti, { opacity: 0, scale: .3 }, bounce, `<${GAP.confetti}`);
    };

    // 画面に出ている画像が揃ってから始める。
    // 揃わなくても IMG_WAIT_CAP で打ち切って必ず再生する
    const imgs = q('img').filter(img => img.getClientRects().length > 0);
    Promise.race([
      Promise.allSettled(imgs.map(img => img.decode())),
      new Promise(resolve => setTimeout(resolve, IMG_WAIT_CAP))
    ]).then(play);
  }
}

export default kvScene;
